import z from 'zod';

import { assertNever } from '../assertNever';
import { zodDateOrString } from '../utils/Zod';
import { NonPerformanceCode } from './NonPerformanceCode';

export const NonPerformanceEventDate = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('plainDateRange'),
    startDate: zodDateOrString,
    endDate: zodDateOrString.optional(),
  }),
  z.object({
    type: z.literal('plainDate'),
    date: zodDateOrString,
  }),
  z.object({
    type: z.literal('timestamp'),
    timestamp: zodDateOrString,
  }),
  z.object({
    type: z.literal('none'),
  }),
]);

export type NonPerformanceEventDate = z.infer<typeof NonPerformanceEventDate>;

export function nonPerformanceEventDatesAreEqual(
  event1: NonPerformanceEventDate,
  event2: NonPerformanceEventDate,
): boolean {
  switch (event1.type) {
    case 'plainDateRange':
      if (event2.type !== 'plainDateRange') {
        return false;
      }
      return (
        event1.startDate.getTime() === event2.startDate.getTime() &&
        event1.endDate?.getTime() === event2.endDate?.getTime()
      );
    case 'plainDate':
      if (event2.type !== 'plainDate') {
        return false;
      }
      return event1.date.getTime() === event2.date.getTime();
    case 'timestamp':
      if (event2.type !== 'timestamp') {
        return false;
      }
      return event1.timestamp.getTime() === event2.timestamp.getTime();
    case 'none':
      if (event2.type !== 'none') {
        return false;
      }
      return true;
    default:
      return assertNever(event1);
  }
}

export const NonPerformanceEvent = z.object({
  code: NonPerformanceCode,
  date: NonPerformanceEventDate,
});

export type NonPerformanceEvent = z.infer<typeof NonPerformanceEvent>;

export function nonPerformanceEventSortFn(a: NonPerformanceEvent, b: NonPerformanceEvent): number {
  const dateA = getEventDateForSorting(a.date);
  const dateB = getEventDateForSorting(b.date);
  const dateDiff = dateA.getTime() - dateB.getTime();
  if (dateDiff !== 0) {
    return dateDiff;
  }
  return a.code.localeCompare(b.code);
}

function getEventDateForSorting(date: NonPerformanceEventDate): Date {
  switch (date.type) {
    case 'plainDate':
      return date.date;
    case 'plainDateRange':
      return date.startDate;
    case 'timestamp':
      return date.timestamp;
    case 'none':
      return new Date(0); // Use epoch for 'none' type
    default:
      throw new Error(`Invalid event date type: ${(date as { type: string }).type}`);
  }
}
